import { Link } from 'react-router-dom';
import { Sparkles, Home, Check, Crown } from 'lucide-react';
import { Button, Card, CardContent } from '@nimbitlabs/ui';

interface UpgradeProps {
  title?: string;
  description?: string;
  features?: string[];
  upgradeButtonText?: string;
  upgradeButtonLink?: string;
  dashboardLink?: string;
}

const defaultFeatures = [
  'Advanced incident management',
  'Real-time service monitoring',
  'Custom alert rules and notifications',
  'Automated runbook execution',
  'Full audit trail and compliance',
  'Unlimited services and environments',
];

export default function Upgrade({
  title = 'Premium Feature',
  description = 'This feature is available on paid plans only.',
  features = defaultFeatures,
  upgradeButtonText = 'View Plans',
  upgradeButtonLink = '/settings?tab=billing',
  dashboardLink = '/dashboard',
}: UpgradeProps) {
  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 bg-background/95 backdrop-blur-sm">
      <Card className="w-full max-w-md border-2 shadow-2xl bg-gradient-to-br from-card via-card to-primary/5">
        <CardContent className="p-8 space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg">
                <Crown className="h-10 w-10 text-primary-foreground" />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              {title}
            </h2>
            <p className="text-muted-foreground text-sm">
              {description}
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-foreground/90">Upgrade to unlock:</p>
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-sm">
                  <div className="mt-0.5 flex-shrink-0">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                  <span className="text-foreground/80">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2">
            <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg">
              <Link to={upgradeButtonLink}>
                <Sparkles className="h-4 w-4 mr-2" />
                {upgradeButtonText}
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="w-full">
              <Link to={dashboardLink}>
                <Home className="h-4 w-4 mr-2" />
                Go to Dashboard
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
